import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/Card';
import { Loader } from '@components/Loader';
import { PublicBookingLayout, StatusIcon } from './PublicBookingLayout';
import { RescheduleSection } from './RescheduleSection';
import {
  PublicApiError,
  PublicBooking,
  formatInTimezone,
  publicBookingService,
} from './publicBookingService';

type ViewState = 'loading' | 'ready' | 'invalid' | 'error' | 'cancelled';

const DetailRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
    <dt className="text-sm text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="text-sm font-semibold text-gray-900 dark:text-white text-right">{value}</dd>
  </div>
);

const ManageBooking: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<ViewState>('loading');
  const [booking, setBooking] = useState<PublicBooking | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  const [movedNotice, setMovedNotice] = useState(false);

  const load = useCallback(async () => {
    setView('loading');
    try {
      setBooking(await publicBookingService.getBooking(token));
      setView('ready');
    } catch (error) {
      setView(error instanceof PublicApiError && error.status === 404 ? 'invalid' : 'error');
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleMoved = useCallback((moved: PublicBooking) => {
    setBooking(moved);
    setRescheduling(false);
    setCancelError('');
    setMovedNotice(true);
  }, []);

  const handleGone = useCallback(() => setView('invalid'), []);

  // After a conflict the appointment may have started; refresh silently.
  const handleConflict = useCallback(async () => {
    try {
      const fresh = await publicBookingService.getBooking(token);
      setBooking(fresh);
      if (!fresh.can_reschedule) setRescheduling(false);
    } catch {
      // Keep the current view; the picker already shows an error
    }
  }, [token]);

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError('');
    try {
      await publicBookingService.cancelBooking(token);
      setConfirming(false);
      setView('cancelled');
    } catch (error) {
      setConfirming(false);
      if (error instanceof PublicApiError && error.status === 404) {
        setView('invalid');
      } else if (error instanceof PublicApiError && error.status === 409) {
        setCancelError(
          error.code === 'profile_inactive'
            ? t('publicBooking.manage.inactiveText')
            : t('publicBooking.manage.startedText')
        );
        setBooking(prev => (prev ? { ...prev, can_cancel: false, can_reschedule: false } : prev));
      } else {
        setCancelError(t('publicBooking.manage.errorText'));
      }
    } finally {
      setCancelling(false);
    }
  };

  if (view === 'loading') {
    return (
      <PublicBookingLayout>
        <Loader message={t('publicBooking.loading')} size="md" />
      </PublicBookingLayout>
    );
  }

  if (view === 'invalid' || view === 'error') {
    const isInvalid = view === 'invalid';
    return (
      <PublicBookingLayout>
        <Card className="shadow-2xl">
          <CardHeader className="text-center pb-8">
            <StatusIcon icon={isInvalid ? 'link_off' : 'error_outline'} tone="error" />
            <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">
              {isInvalid ? t('publicBooking.manage.invalidTitle') : t('publicBooking.manage.title')}
            </CardTitle>
            <CardDescription className="text-gray-600 dark:text-gray-400">
              {isInvalid
                ? t('publicBooking.manage.invalidText')
                : t('publicBooking.manage.errorText')}
            </CardDescription>
          </CardHeader>
          {!isInvalid && (
            <CardContent className="px-8 pb-8">
              <Button variant="gradient-multi" size="lg" className="w-full" onClick={load}>
                {t('publicBooking.manage.retry')}
              </Button>
            </CardContent>
          )}
        </Card>
      </PublicBookingLayout>
    );
  }

  if (view === 'cancelled') {
    return (
      <PublicBookingLayout>
        <Card className="shadow-2xl">
          <CardHeader className="text-center pb-8">
            <StatusIcon icon="event_busy" tone="success" />
            <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('publicBooking.manage.cancelledTitle')}
            </CardTitle>
            <CardDescription className="text-gray-600 dark:text-gray-400">
              {t('publicBooking.manage.cancelledText')}
            </CardDescription>
          </CardHeader>
        </Card>
      </PublicBookingLayout>
    );
  }

  if (!booking) return null;

  const locale = i18n.language;
  const date = formatInTimezone(booking.start, booking.timezone, locale, {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
  const timeOptions: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  const time = t('publicBooking.manage.timeValue', {
    start: formatInTimezone(booking.start, booking.timezone, locale, timeOptions),
    end: formatInTimezone(booking.end, booking.timezone, locale, timeOptions),
  });

  return (
    <PublicBookingLayout>
      <Card className="shadow-2xl">
        <CardHeader className="text-center pb-6">
          <StatusIcon icon="event_available" />
          <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('publicBooking.manage.title')}
          </CardTitle>
          <CardDescription className="text-lg text-gray-600 dark:text-gray-400">
            {booking.profile_name}
          </CardDescription>
        </CardHeader>

        <CardContent className="px-8 pb-8 space-y-6">
          <dl>
            <DetailRow label={t('publicBooking.manage.service')} value={booking.service_name} />
            {booking.variant_name && (
              <DetailRow label={t('publicBooking.manage.variant')} value={booking.variant_name} />
            )}
            {booking.resource_name && (
              <DetailRow label={t('publicBooking.manage.resource')} value={booking.resource_name} />
            )}
            <DetailRow label={t('publicBooking.manage.date')} value={date} />
            <DetailRow label={t('publicBooking.manage.time')} value={time} />
            <DetailRow label={t('publicBooking.manage.customer')} value={booking.customer_name} />
          </dl>

          {movedNotice && !rescheduling && (
            <div
              role="status"
              className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3"
            >
              <p className="text-sm text-green-700 dark:text-green-400">
                {t('publicBooking.manage.movedText')}
              </p>
            </div>
          )}

          {cancelError && (
            <div
              role="alert"
              className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3"
            >
              <p className="text-sm text-red-600 dark:text-red-400">{cancelError}</p>
            </div>
          )}

          {booking.can_reschedule && !rescheduling && !confirming && (
            <Button
              variant="gradient-multi"
              size="lg"
              className="w-full"
              onClick={() => {
                setMovedNotice(false);
                setRescheduling(true);
              }}
            >
              {t('publicBooking.manage.rescheduleButton')}
            </Button>
          )}

          {booking.can_reschedule && rescheduling && (
            <RescheduleSection
              token={token}
              timezone={booking.timezone}
              onMoved={handleMoved}
              onClose={() => setRescheduling(false)}
              onConflict={handleConflict}
              onGone={handleGone}
            />
          )}

          {booking.can_cancel && !confirming && !rescheduling && (
            <Button
              variant="danger"
              size="lg"
              className="w-full"
              onClick={() => setConfirming(true)}
            >
              {t('publicBooking.manage.cancelButton')}
            </Button>
          )}

          {!booking.can_cancel && !cancelError && (
            <p className="text-sm text-center text-gray-500 dark:text-gray-400">
              {t('publicBooking.manage.cannotCancel')}
            </p>
          )}

          {booking.can_cancel && confirming && (
            <div
              role="alertdialog"
              aria-labelledby="cancel-confirm-title"
              className="rounded-lg border border-red-200 dark:border-red-800 p-4 space-y-4"
            >
              <div>
                <p
                  id="cancel-confirm-title"
                  className="font-semibold text-gray-900 dark:text-white"
                >
                  {t('publicBooking.manage.cancelConfirmTitle')}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('publicBooking.manage.cancelConfirmText')}
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setConfirming(false)}
                  disabled={cancelling}
                >
                  {t('publicBooking.manage.cancelConfirmNo')}
                </Button>
                <Button
                  variant="danger"
                  className="flex-1"
                  onClick={handleCancel}
                  loading={cancelling}
                  disabled={cancelling}
                >
                  {cancelling
                    ? t('publicBooking.manage.cancelling')
                    : t('publicBooking.manage.cancelConfirmYes')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </PublicBookingLayout>
  );
};

export { ManageBooking };
